import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import {
  createMirrorSession,
  getMirrorSession,
  checkMemoryPair,
  completeMemory,
  getAssociationPieces,
  checkAssociation,
  completeAssociation,
  getProfile,
} from '../../api';
import './MiroirEmotions.css';

const EMOJI_MAP = {
  'Joie': '😊',
  'Tristesse': '😢',
  'Colère': '😠',
  'Colere': '😠',
  'Peur': '😨',
  'Surprise': '😲',
  'Dégoût': '🤢',
  'Degout': '🤢',
  'Timidité': '😳',
  'Timidite': '😳',
  'Jalousie': '😒',
  'Frustration': '😤',
  'Sérénité': '😌',
  'Serenite': '😌',
};

function getEmoji(name) {
  return EMOJI_MAP[name] || '🙂';
}

function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function MiroirEmotions() {
  const { token, saveAuth } = useAuth();
  const navigate = useNavigate();

  // Global state
  const [phase, setPhase] = useState('selection');
  const [sessionId, setSessionId] = useState(null);
  const [emotions, setEmotions] = useState([]);
  const [difficultyLevel, setDifficultyLevel] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Memory state
  const [cards, setCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [memoryPairsFound, setMemoryPairsFound] = useState(0);
  const [memoryErrors, setMemoryErrors] = useState(0);
  const [totalPairs, setTotalPairs] = useState(0);
  const [isChecking, setIsChecking] = useState(false);

  // Association state
  const [associationEmotions, setAssociationEmotions] = useState([]);
  const [targets, setTargets] = useState([]);
  const [matchedAssociations, setMatchedAssociations] = useState(new Set());
  const [associationPairsFound, setAssociationPairsFound] = useState(0);
  const [associationErrors, setAssociationErrors] = useState(0);
  const [feedbackActions, setFeedbackActions] = useState(null);
  const [draggedEmotionId, setDraggedEmotionId] = useState(null);
  const [shakeTargetId, setShakeTargetId] = useState(null);
  const [matchedTargetIds, setMatchedTargetIds] = useState(new Set());
  const [associationTotalPairs, setAssociationTotalPairs] = useState(0);
  const [isCheckingAssociation, setIsCheckingAssociation] = useState(false);

  // Results state
  const [score, setScore] = useState(0);
  const [maxScore, setMaxScore] = useState(100);
  const [xpEarned, setXpEarned] = useState(0);
  const [isNewBestScore, setIsNewBestScore] = useState(false);

  // --- Phase Selection ---
  const handleSelectDifficulty = async (level) => {
    setLoading(true);
    setError('');
    try {
      const response = await createMirrorSession(token, level);
      const data = response.data;
      setSessionId(data.session_id);
      setEmotions(data.emotions);
      setDifficultyLevel(level);
      initMemoryCards(data.emotions);
      setTotalPairs(data.emotions.length);
      setPhase('memory');
    } catch (err) {
      if (err.status === 422 && err.data?.session_id) {
        // Session already in progress — resume it
        try {
          await resumeSession(err.data.session_id);
        } catch {
          setError('Erreur lors de la reprise de session');
        }
      } else {
        setError(err.message || 'Erreur lors de la création de la session');
      }
    } finally {
      setLoading(false);
    }
  };

  const resumeSession = async (sid) => {
    try {
      const response = await getMirrorSession(token, sid);
      const data = response.data;
      const session = data.session;
      const emos = data.emotions;

      setSessionId(session.id);
      setEmotions(emos);
      setDifficultyLevel(session.difficulty_level);

      if (session.association_completed) {
        // Game is done, go to results (shouldn't normally happen)
        setPhase('selection');
      } else if (session.memory_completed) {
        // Memory done, go to association
        setMemoryPairsFound(session.memory_pairs_found);
        setMemoryErrors(session.memory_errors);
        await startAssociationPhase(session.id);
      } else {
        // Memory in progress
        setMemoryPairsFound(session.memory_pairs_found);
        setMemoryErrors(session.memory_errors);
        setTotalPairs(emos.length);
        initMemoryCards(emos);
        setPhase('memory');
      }
    } catch {
      setError('Erreur lors de la reprise de session');
    }
  };

  const initMemoryCards = (emotionList) => {
    const cardPairs = [];
    emotionList.forEach((emotion) => {
      cardPairs.push({
        id: `face-${emotion.id}`,
        emotionId: emotion.id,
        type: 'face',
        content: getEmoji(emotion.name),
        isFlipped: false,
        isMatched: false,
      });
      cardPairs.push({
        id: `word-${emotion.id}`,
        emotionId: emotion.id,
        type: 'word',
        content: emotion.name,
        isFlipped: false,
        isMatched: false,
      });
    });
    setCards(shuffleArray(cardPairs));
  };

  // --- Phase Memory ---
  const handleCardClick = useCallback(async (index) => {
    if (isChecking) return;
    if (cards[index].isFlipped || cards[index].isMatched) return;
    if (selectedCards.length >= 2) return;

    const newCards = [...cards];
    newCards[index] = { ...newCards[index], isFlipped: true };
    setCards(newCards);

    const newSelected = [...selectedCards, index];
    setSelectedCards(newSelected);

    if (newSelected.length === 2) {
      setIsChecking(true);
      const card1 = newCards[newSelected[0]];
      const card2 = newCards[newSelected[1]];

      // Determine which is face and which is word
      let faceCard, wordCard;
      if (card1.type === 'face' && card2.type === 'word') {
        faceCard = card1;
        wordCard = card2;
      } else if (card1.type === 'word' && card2.type === 'face') {
        faceCard = card2;
        wordCard = card1;
      } else {
        // Two cards of same type — always incorrect, flip back
        setTimeout(() => {
          const resetCards = [...newCards];
          resetCards[newSelected[0]] = { ...resetCards[newSelected[0]], isFlipped: false };
          resetCards[newSelected[1]] = { ...resetCards[newSelected[1]], isFlipped: false };
          setCards(resetCards);
          setSelectedCards([]);
          setIsChecking(false);
          setMemoryErrors((prev) => prev + 1);
        }, 1000);
        return;
      }

      try {
        const response = await checkMemoryPair(
          token,
          sessionId,
          faceCard.emotionId,
          wordCard.emotionId
        );
        const result = response.data;

        if (result.correct) {
          const matchedCards = [...newCards];
          matchedCards[newSelected[0]] = { ...matchedCards[newSelected[0]], isMatched: true };
          matchedCards[newSelected[1]] = { ...matchedCards[newSelected[1]], isMatched: true };
          setCards(matchedCards);
          setMemoryPairsFound(result.memory_pairs_found);
          setMemoryErrors(result.memory_errors);

          // Check if all pairs found
          if (result.memory_pairs_found >= result.total_pairs) {
            setTimeout(async () => {
              try {
                await completeMemory(token, sessionId);
                await startAssociationPhase(sessionId);
              } catch {
                setError('Erreur lors de la transition vers la phase Association');
              }
            }, 800);
          }
        } else {
          setMemoryErrors(result.memory_errors);
          setTimeout(() => {
            const resetCards = [...newCards];
            resetCards[newSelected[0]] = { ...resetCards[newSelected[0]], isFlipped: false };
            resetCards[newSelected[1]] = { ...resetCards[newSelected[1]], isFlipped: false };
            setCards(resetCards);
          }, 1000);
        }
      } catch {
        setError('Erreur lors de la vérification');
        // Flip cards back on error
        setTimeout(() => {
          const resetCards = [...newCards];
          resetCards[newSelected[0]] = { ...resetCards[newSelected[0]], isFlipped: false };
          resetCards[newSelected[1]] = { ...resetCards[newSelected[1]], isFlipped: false };
          setCards(resetCards);
        }, 1000);
      } finally {
        setTimeout(() => {
          setSelectedCards([]);
          setIsChecking(false);
        }, 1000);
      }
    }
  }, [cards, selectedCards, isChecking, token, sessionId]);

  // --- Phase Association ---
  const startAssociationPhase = async (sid) => {
    try {
      const response = await getAssociationPieces(token, sid);
      const data = response.data;
      setAssociationEmotions(shuffleArray(data.emotions));
      setTargets(shuffleArray(data.targets));
      setAssociationTotalPairs(data.emotions.length);
      setMatchedAssociations(new Set());
      setMatchedTargetIds(new Set());
      setAssociationPairsFound(0);
      setAssociationErrors(0);
      setPhase('association');
    } catch {
      setError('Erreur lors du chargement de la phase Association');
    }
  };

  const handleDragStart = (e, emotionId) => {
    if (isCheckingAssociation) return;
    setDraggedEmotionId(emotionId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', emotionId.toString());
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (isCheckingAssociation) return;
    const emotionId = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (!emotionId || matchedAssociations.has(emotionId)) return;

    setIsCheckingAssociation(true);
    try {
      const response = await checkAssociation(token, sessionId, emotionId, targetId);
      const result = response.data;

      if (result.correct) {
        const newMatched = new Set(matchedAssociations);
        newMatched.add(emotionId);
        setMatchedAssociations(newMatched);
        const newMatchedTargets = new Set(matchedTargetIds);
        newMatchedTargets.add(targetId);
        setMatchedTargetIds(newMatchedTargets);
        setAssociationPairsFound(result.association_pairs_found);
        setAssociationErrors(result.association_errors);

        // Show feedback actions
        if (result.actions && result.actions.length > 0) {
          setFeedbackActions(result.actions);
          setTimeout(() => setFeedbackActions(null), 3000);
        }

        // Check if all associations found
        if (result.association_pairs_found >= result.total_pairs) {
          setTimeout(async () => {
            try {
              const completeResponse = await completeAssociation(token, sessionId);
              const completeData = completeResponse.data;
              setScore(completeData.score);
              setMaxScore(completeData.max_score);
              setXpEarned(completeData.xp_earned);
              setIsNewBestScore(completeData.is_new_best_score);
              // Refresh user profile to update XP
              try {
                const profileResponse = await getProfile(token);
                saveAuth(profileResponse.data, token);
              } catch { /* ignore profile refresh errors */ }
              setPhase('results');
            } catch {
              setError('Erreur lors de la finalisation du jeu');
            }
          }, 1000);
        }
      } else {
        setAssociationErrors(result.association_errors);
        setShakeTargetId(targetId);
        setTimeout(() => setShakeTargetId(null), 600);
      }
    } catch {
      setError('Erreur lors de la vérification');
    } finally {
      setDraggedEmotionId(null);
      setIsCheckingAssociation(false);
    }
  };

  // Touch support for drag & drop
  const [touchDragEmotion, setTouchDragEmotion] = useState(null);

  const handleTouchStart = (emotionId) => {
    if (isCheckingAssociation || matchedAssociations.has(emotionId)) return;
    setTouchDragEmotion(emotionId);
  };

  const handleTouchDrop = async (targetId) => {
    if (!touchDragEmotion || isCheckingAssociation) return;
    setIsCheckingAssociation(true);
    try {
      const response = await checkAssociation(token, sessionId, touchDragEmotion, targetId);
      const result = response.data;

      if (result.correct) {
        const newMatched = new Set(matchedAssociations);
        newMatched.add(touchDragEmotion);
        setMatchedAssociations(newMatched);
        const newMatchedTargets = new Set(matchedTargetIds);
        newMatchedTargets.add(targetId);
        setMatchedTargetIds(newMatchedTargets);
        setAssociationPairsFound(result.association_pairs_found);
        setAssociationErrors(result.association_errors);

        if (result.actions && result.actions.length > 0) {
          setFeedbackActions(result.actions);
          setTimeout(() => setFeedbackActions(null), 3000);
        }

        if (result.association_pairs_found >= result.total_pairs) {
          setTimeout(async () => {
            try {
              const completeResponse = await completeAssociation(token, sessionId);
              const completeData = completeResponse.data;
              setScore(completeData.score);
              setMaxScore(completeData.max_score);
              setXpEarned(completeData.xp_earned);
              setIsNewBestScore(completeData.is_new_best_score);
              try {
                const profileResponse = await getProfile(token);
                saveAuth(profileResponse.data, token);
              } catch { /* ignore profile refresh errors */ }
              setPhase('results');
            } catch {
              setError('Erreur lors de la finalisation du jeu');
            }
          }, 1000);
        }
      } else {
        setAssociationErrors(result.association_errors);
        setShakeTargetId(targetId);
        setTimeout(() => setShakeTargetId(null), 600);
      }
    } catch {
      setError('Erreur lors de la vérification');
    } finally {
      setTouchDragEmotion(null);
      setIsCheckingAssociation(false);
    }
  };

  // --- Phase Results ---
  const handleReplay = () => {
    setPhase('selection');
    setSessionId(null);
    setEmotions([]);
    setDifficultyLevel(null);
    setCards([]);
    setSelectedCards([]);
    setMemoryPairsFound(0);
    setMemoryErrors(0);
    setTotalPairs(0);
    setIsChecking(false);
    setAssociationEmotions([]);
    setTargets([]);
    setMatchedAssociations(new Set());
    setMatchedTargetIds(new Set());
    setAssociationPairsFound(0);
    setAssociationErrors(0);
    setFeedbackActions(null);
    setDraggedEmotionId(null);
    setAssociationTotalPairs(0);
    setScore(0);
    setMaxScore(100);
    setXpEarned(0);
    setIsNewBestScore(false);
    setError('');
  };

  // --- Render ---
  return (
    <div className="miroir-page">
      <header className="miroir-header">
        <h1>Le Miroir des Émotions</h1>
        {phase !== 'selection' && phase !== 'results' && (
          <span className="miroir-phase-badge">
            {phase === 'memory' ? 'Phase 1 : Memory' : 'Phase 2 : Association'}
          </span>
        )}
      </header>

      {error && (
        <div className="miroir-error">
          <p>{error}</p>
          <button onClick={() => setError('')}>Fermer</button>
        </div>
      )}

      <main className="miroir-content">
        {/* SELECTION */}
        {phase === 'selection' && (
          <div className="miroir-selection">
            <p className="miroir-intro">
              Choisis ton niveau de difficulté pour commencer le jeu !
            </p>
            <div className="miroir-difficulty-grid">
              <button
                className="miroir-difficulty-card"
                onClick={() => handleSelectDifficulty(1)}
                disabled={loading}
              >
                <span className="difficulty-icon">⭐</span>
                <h3>Niveau 1</h3>
                <p>6 émotions de base</p>
                <span className="difficulty-label">Débutant</span>
              </button>
              <button
                className="miroir-difficulty-card"
                onClick={() => handleSelectDifficulty(2)}
                disabled={loading}
              >
                <span className="difficulty-icon">⭐⭐</span>
                <h3>Niveau 2</h3>
                <p>10 émotions</p>
                <span className="difficulty-label">Intermédiaire</span>
              </button>
            </div>
            {loading && <p className="miroir-loading">Création de la session...</p>}
          </div>
        )}

        {/* MEMORY */}
        {phase === 'memory' && (
          <div className="miroir-memory">
            <div className="miroir-stats">
              <span className="stat-item stat-success">
                Paires trouvées : {memoryPairsFound}/{totalPairs}
              </span>
              <span className="stat-item stat-error">
                Erreurs : {memoryErrors}
              </span>
            </div>
            <div className={`miroir-memory-grid ${difficultyLevel === 2 ? 'grid-level-2' : 'grid-level-1'}`}>
              {cards.map((card, index) => (
                <div
                  key={card.id}
                  className={`memory-card ${card.isFlipped || card.isMatched ? 'flipped' : ''} ${card.isMatched ? 'matched' : ''}`}
                  onClick={() => handleCardClick(index)}
                >
                  <div className="memory-card-inner">
                    <div className="memory-card-front">
                      <span>?</span>
                    </div>
                    <div className={`memory-card-back ${card.type === 'face' ? 'card-face' : 'card-word'}`}>
                      {card.type === 'face' ? (
                        <span className="card-emoji">{card.content}</span>
                      ) : (
                        <span className="card-text">{card.content}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ASSOCIATION */}
        {phase === 'association' && (
          <div className="miroir-association">
            <div className="miroir-stats">
              <span className="stat-item stat-success">
                Associations : {associationPairsFound}/{associationTotalPairs}
              </span>
              <span className="stat-item stat-error">
                Erreurs : {associationErrors}
              </span>
            </div>

            {touchDragEmotion && (
              <div className="touch-indicator">
                <span>{getEmoji(associationEmotions.find(e => e.id === touchDragEmotion)?.name || '')}</span>
                <p>Touche une bulle pour associer</p>
                <button className="touch-cancel" onClick={() => setTouchDragEmotion(null)}>Annuler</button>
              </div>
            )}

            <div className="miroir-association-layout">
              <div className="association-emotions">
                <h3>Émotions</h3>
                <div className="emotions-list">
                  {associationEmotions.map((emotion) => (
                    <div
                      key={emotion.id}
                      className={`emotion-piece ${matchedAssociations.has(emotion.id) ? 'piece-matched' : ''} ${draggedEmotionId === emotion.id ? 'piece-dragging' : ''} ${touchDragEmotion === emotion.id ? 'piece-selected' : ''}`}
                      draggable={!matchedAssociations.has(emotion.id) && !isCheckingAssociation}
                      onDragStart={(e) => handleDragStart(e, emotion.id)}
                      onDragEnd={() => setDraggedEmotionId(null)}
                      onClick={() => handleTouchStart(emotion.id)}
                    >
                      <span className="piece-emoji">{getEmoji(emotion.name)}</span>
                      <span className="piece-name">{emotion.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="association-targets">
                <h3>Dialogues</h3>
                <div className="targets-list">
                  {targets.map((target) => (
                    <div
                      key={target.id}
                      className={`target-bubble ${matchedTargetIds.has(target.id) ? 'target-matched' : ''} ${shakeTargetId === target.id ? 'shake' : ''}`}
                      onDragOver={!matchedTargetIds.has(target.id) ? handleDragOver : undefined}
                      onDrop={!matchedTargetIds.has(target.id) ? (e) => handleDrop(e, target.id) : undefined}
                      onClick={() => !matchedTargetIds.has(target.id) && touchDragEmotion && handleTouchDrop(target.id)}
                    >
                      <p>{target.content}</p>
                      {matchedTargetIds.has(target.id) && <span className="target-check">&#10003;</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {feedbackActions && (
              <div className="miroir-feedback">
                <h4>Actions à essayer :</h4>
                <ul>
                  {feedbackActions.map((action, i) => (
                    <li key={i}>{action}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* RESULTS */}
        {phase === 'results' && (
          <div className="miroir-results">
            <div className="results-trophy">🏆</div>
            <h2>Félicitations !</h2>

            {isNewBestScore && (
              <div className="results-new-best">
                Nouveau meilleur score !
              </div>
            )}

            <div className="results-score-circle">
              <span className="score-value">{score}</span>
              <span className="score-max">/ {maxScore}</span>
            </div>

            <div className="results-xp">
              <span>+{xpEarned} XP gagnés !</span>
            </div>

            <div className="results-details">
              <div className="result-detail">
                <span className="detail-label">Phase Memory</span>
                <span className="detail-value">{memoryErrors} erreur{memoryErrors !== 1 ? 's' : ''}</span>
              </div>
              <div className="result-detail">
                <span className="detail-label">Phase Association</span>
                <span className="detail-value">{associationErrors} erreur{associationErrors !== 1 ? 's' : ''}</span>
              </div>
            </div>

            <div className="results-actions">
              <button className="results-btn btn-replay" onClick={handleReplay}>
                Rejouer
              </button>
              <button className="results-btn btn-back" onClick={() => navigate('/app/jeux')}>
                Retour aux jeux
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default MiroirEmotions;
